import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'

import { CategoriaAuditoria, RegistroAuditoriaItem } from 'src/dto'
import { Api } from 'src/servicos'

import { AutenticacaoContainer } from '../autenticacao'
import { Botao } from '../botao'
import { Carregando } from '../carregando'
import { FuncoesDataHora } from '../funcoes'
import {
  AcoesRodape,
  Container,
  Filtro,
  ItemTimeline,
  Mensagem,
  NotaInicio,
  OpcaoFiltro,
  TagCategoria,
  Timeline
} from './styles'
import {
  descreverAlteracao,
  descreverUsuario,
  formatarInicioAuditoria,
  HistoricoAuditoriaProps,
  HistoricoAuditoriaRef,
  ROTULOS_CATEGORIA
} from './tipos'

const TAMANHO_PADRAO = 50

// Mesmos perfis do [Authorize] do AuditoriaController; os demais (ex.: Parceiro) tomariam 403.
const PERFIS_COM_ACESSO = ['Comercial', 'Financeiro', 'Academico', 'Gerencial']

// Para as telas esconderem o bloco (ex.: o Accordion) sem bloquear o acesso à página.
export const usePodeVerHistoricoAuditoria = (): boolean => {
  const { perfil } = AutenticacaoContainer.useContainer()
  return (perfil?.perfis ?? []).some(p => PERFIS_COM_ACESSO.includes(p))
}

const HistoricoAuditoriaComponent: React.ForwardRefRenderFunction<
  HistoricoAuditoriaRef,
  HistoricoAuditoriaProps
> = (
  {
    tipoEntidade,
    entidadeId,
    opcoesCategoria,
    categoriaFixa,
    semContexto = false,
    tamanhoPagina = TAMANHO_PADRAO,
    resumido = false,
    aoCarregar
  },
  ref
) => {
  const temAcesso = usePodeVerHistoricoAuditoria()

  const [itens, definirItens] = useState<RegistroAuditoriaItem[]>([])
  const [proximoCursor, definirProximoCursor] = useState<string | null>(null)
  const [categoria, definirCategoria] = useState<CategoriaAuditoria | null>(
    categoriaFixa ?? null
  )
  // Só as categorias que existem na trilha viram opção de filtro; mostrar todas sugeriria registros
  // que a entidade não tem.
  const [categoriasDisponiveis, definirCategoriasDisponiveis] = useState<
    CategoriaAuditoria[]
  >([])
  const [carregando, definirCarregando] = useState(true)
  const [carregandoMais, definirCarregandoMais] = useState(false)
  const [erro, definirErro] = useState(false)
  // Descarta respostas antigas quando o filtro/entidade muda durante uma requisição.
  const requisicaoAtual = useRef(0)
  // Em ref: a função da tela pode mudar a cada render e não deve disparar novo carregamento.
  const aoCarregarRef = useRef(aoCarregar)
  aoCarregarRef.current = aoCarregar

  const carregarPrimeiraPagina = useCallback(async () => {
    if (!temAcesso) return

    const requisicao = ++requisicaoAtual.current
    definirCarregando(true)
    definirErro(false)

    try {
      const resposta = await Api.ObterRegistrosAuditoria(
        tipoEntidade,
        entidadeId,
        { categoria: categoria ?? undefined, tamanho: tamanhoPagina }
      )
      if (requisicao !== requisicaoAtual.current) return

      definirItens(resposta.itens ?? [])
      definirProximoCursor(resposta.proximoCursor ?? null)
      if (resposta.categoriasDisponiveis) {
        definirCategoriasDisponiveis(resposta.categoriasDisponiveis)
      }
      aoCarregarRef.current?.((resposta.itens ?? []).length)
    } catch {
      if (requisicao !== requisicaoAtual.current) return
      definirErro(true)
    } finally {
      if (requisicao === requisicaoAtual.current) definirCarregando(false)
    }
  }, [tipoEntidade, entidadeId, categoria, tamanhoPagina, temAcesso])

  const carregarMais = async () => {
    if (proximoCursor === null) return

    const requisicao = ++requisicaoAtual.current
    definirCarregandoMais(true)

    try {
      const resposta = await Api.ObterRegistrosAuditoria(
        tipoEntidade,
        entidadeId,
        {
          categoria: categoria ?? undefined,
          cursor: proximoCursor,
          tamanho: tamanhoPagina
        }
      )
      if (requisicao !== requisicaoAtual.current) return

      definirItens(anteriores => anteriores.concat(resposta.itens ?? []))
      definirProximoCursor(resposta.proximoCursor ?? null)
    } catch {
      if (requisicao !== requisicaoAtual.current) return
      definirErro(true)
    } finally {
      if (requisicao === requisicaoAtual.current) definirCarregandoMais(false)
    }
  }

  useImperativeHandle(ref, () => ({ recarregar: carregarPrimeiraPagina }), [
    carregarPrimeiraPagina
  ])

  useEffect(() => {
    carregarPrimeiraPagina()
  }, [carregarPrimeiraPagina])

  // O filtro aparece mesmo com uma única categoria: mostra de que tipo é o histórico.
  const opcoesVisiveis = (opcoesCategoria ?? []).filter(opcao =>
    categoriasDisponiveis.includes(opcao.categoria)
  )

  const selecionarCategoria = (nova: CategoriaAuditoria | null) => {
    if (nova === categoria) return
    definirCategoria(nova)
  }

  if (!temAcesso) return null

  return (
    <Container>
      <NotaInicio>
        Registro de alterações iniciado em {formatarInicioAuditoria()}.
        Alterações anteriores a essa data não constam neste histórico.
      </NotaInicio>
      {opcoesVisiveis.length > 0 && (
        <Filtro>
          <OpcaoFiltro
            type="button"
            ativo={categoria === null}
            onClick={() => selecionarCategoria(null)}
          >
            Todas
          </OpcaoFiltro>
          {opcoesVisiveis.map(opcao => (
            <OpcaoFiltro
              key={opcao.categoria}
              type="button"
              ativo={categoria === opcao.categoria}
              onClick={() => selecionarCategoria(opcao.categoria)}
            >
              {opcao.texto}
            </OpcaoFiltro>
          ))}
        </Filtro>
      )}

      {carregando && <Carregando texto="Carregando histórico ..." />}

      {!carregando && erro && (
        <Mensagem>
          Não foi possível carregar o histórico de alterações.{' '}
          <Botao
            texto="Tentar novamente"
            tema="Secundario"
            tamanho="S"
            type="button"
            onClick={carregarPrimeiraPagina}
          />
        </Mensagem>
      )}

      {!carregando && !erro && itens.length === 0 && (
        <Mensagem>Nenhuma alteração registrada.</Mensagem>
      )}

      {!carregando && itens.length > 0 && (
        <Timeline>
          {itens.map((item, indice) => (
            <ItemTimeline
              key={`${item.correlacaoId ?? 'sem-correlacao'}-${
                item.dataHora
              }-${indice}`}
            >
              {opcoesCategoria && (
                <TagCategoria>
                  {ROTULOS_CATEGORIA[item.categoria] ?? item.categoria}
                </TagCategoria>
              )}
              <span className="titulo">
                {item.acaoDescricao}
                {!resumido && !semContexto && item.contexto?.descricao && (
                  <span className="contexto"> · {item.contexto.descricao}</span>
                )}
              </span>
              <span className="meta">
                {descreverUsuario(item.usuario, item.impersonadoPor)} ·{' '}
                {FuncoesDataHora.dataHoraToLocaleString(item.dataHora)}
              </span>
              {!resumido &&
                item.alteracoes?.map((alteracao, i) => (
                  <span key={`${alteracao.campo}-${i}`} className="alteracao">
                    {descreverAlteracao(alteracao, item.contexto)}
                  </span>
                ))}
              {!resumido && item.observacao && (
                <div className="observacao">{item.observacao}</div>
              )}
            </ItemTimeline>
          ))}
        </Timeline>
      )}

      {!carregando && proximoCursor !== null && (
        <AcoesRodape>
          <Botao
            texto="Carregar mais"
            tema="Secundario"
            type="button"
            carregando={carregandoMais}
            disabled={carregandoMais}
            onClick={carregarMais}
          />
        </AcoesRodape>
      )}
    </Container>
  )
}

export const HistoricoAuditoria = forwardRef(HistoricoAuditoriaComponent)

export * from './tipos'
